import * as Sentry from '@sentry/vue';

import config from '../config';

const LEVEL_INFO = 'info';
const LEVEL_WARNING = 'warning';
const LEVEL_ERROR = 'error';
const MESSAGE_LEVEL_MAP = [
    {
        level: LEVEL_WARNING,
        message: /^Network Error$/,
    },
];

function shouldBeLogged() {
    return config.sentry.active;
}

function levelFromMessage(message = '') {
    const match = MESSAGE_LEVEL_MAP.find(x => message.match(x.message));

    return match ? match.level : null;
}

/**
 * Log exception to sentry.
 * @param {object} exception - The exception that gets thrown.
 * @param {extra} extra - Extra data that is sent with the event.
 * @param {string[]} fingerprint - Used to group events in Sentry.
 * @param {severity} level
 * @param {string} message - Custom exception message.
 * @param {object} tags - Used to organize/group events in Sentry.
 */
function log({
    exception,
    extra,
    fingerprint,
    level,
    message,
    tags,
}) {
    // eslint-disable-next-line no-console
    if (import.meta.env.MODE === 'development') console.error(exception || message);

    if (!shouldBeLogged() || (exception && exception.skipLogging)) return null;

    Sentry.withScope((scope) => {
        if (extra) {
            Object.keys(extra)
                .forEach(extraName => scope.setExtra(extraName, extra[extraName]));
        }
        if (fingerprint) {
            scope.setFingerprint(fingerprint);
        }
        if (tags) {
            Object.keys(tags)
                .forEach(tagName => scope.setTag(tagName, tags[tagName]));
        }
        if (exception && Object.keys(exception).length > 0) {
            // eslint-disable-next-line no-param-reassign
            exception.message = message || exception.message;
            scope.setLevel(level || levelFromMessage(exception.message));
            Sentry.captureException(exception);
        } else {
            scope.setLevel(level);
            const errorMessage = message || 'Unknown error occured';
            Sentry.captureMessage(errorMessage);
        }
    });

    return Sentry.lastEventId();
}

export function info(options) {
    return log({
        ...options,
        level: LEVEL_INFO,
    });
}

export function warning(options) {
    return log({
        ...options,
        level: LEVEL_WARNING,
    });
}

export function error(options) {
    return log({
        ...options,
        level: LEVEL_ERROR,
    });
}
